import React, { useState } from "react";
import SubCard from "../../../ui-component/cards/SubCard";
import { 
  Button, 
  Grid, 
  TextField, 
  Box, 
  Typography, 
  Snackbar, 
  Alert, 
  CircularProgress, 
  Switch, 
  FormControlLabel 
} from "@mui/material";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import { Search, Save, Replay } from '@mui/icons-material';

const PincodeManagementSection = () => {
    const [pincode, setPincode] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const initialFormState = {
      areaName: '',
      officeName: '',
      officeType: '',
      tehsil: '',
      district: '',
      cluster: '',
      state: '',
      stateId: '',
      codChargePercentage: '',
      codChargeTruckValue: '',
      stdCode: '',
      pinCodeForPorterShipping: false
    };
    const [formData, setFormData] = useState(initialFormState);
    
    const [snackbar, setSnackbar] = useState({
      open: false,
      message: '',
      severity: 'success'
    });
  
    const [errors, setErrors] = useState({});
  
    const handleSearch = async (e) => {
      e.preventDefault();
      if (!pincode || pincode.length !== 6) {
          setSnackbar({ open: true, message: 'Please enter a valid 6-digit pincode', severity: 'warning' });
          return;
      }
  
      setLoading(true);
      try {
        const baseUrl = process.env.REACT_APP_PATH_BULK || process.env.REACT_APP_PATH;
        const response = await axios.get(`${baseUrl}/role4/addOrUpdatePincode?pincode=${pincode}`);
        const data = response.data.data; 
        
        if (data) {
          setFormData({
              areaName: data.areaName || '',
              officeName: data.officeName || '',
              officeType: data.officeType || '',
              tehsil: data.tehsil || '',
              district: data.district || '',
              cluster: data.cluster || '',
              state: data.state || '',
              stateId: data.stateId || '',
              codChargePercentage: data.codChargePercentage || '',
              codChargeTruckValue: data.codChargeTruckValue || '',
              stdCode: data.stdCode || '',
              pinCodeForPorterShipping: data.pinCodeForPorterShipping || false
          });
          setSnackbar({ open: true, message: 'Pincode details found', severity: 'success' });
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          setFormData({
              areaName: '',
              officeName: '',
              officeType: '',
              tehsil: '',
              district: '',
              cluster: '',
              state: '',
              stateId: '',
              codChargePercentage: '',
              codChargeTruckValue: '',
              stdCode: '',
              pinCodeForPorterShipping: false
          });
          setSnackbar({ open: true, message: 'Pincode not found. You can add it now.', severity: 'info' });
        } else {
          setSnackbar({ open: true, message: error.response?.data?.message || 'Error searching pincode', severity: 'error' });
        }
      } finally {
        setLoading(false);
      }
    };
  
    const handleChange = (e) => {
      const { name, value, checked, type } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
      if (errors[name]) {
          setErrors(prev => ({ ...prev, [name]: null }));
      }
    };
  
    const validate = () => {
        const newErrors = {};
        const requiredFields = [
            'areaName', 'officeName', 'officeType', 'tehsil',
            'district', 'cluster', 'state', 'stateId', 'codChargePercentage',
            'codChargeTruckValue', 'stdCode'
        ];
  
        requiredFields.forEach(field => {
            if (!formData[field] && formData[field] !== 0) {
                newErrors[field] = 'This field is mandatory';
            }
        });
  
        if (!pincode || pincode.length !== 6) {
            newErrors['pincode'] = 'Valid pincode is required';
        }
  
        if (formData.stateId && formData.stateId.length !== 2) {
            newErrors['stateId'] = 'State ID must be 2 characters (e.g., DL)';
        }
  
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
  
    const handleSubmit = async () => {
      if (!validate()) {
          setSnackbar({ open: true, message: 'Please fill all mandatory fields correctly', severity: 'warning' });
          return;
      }
  
      setSaving(true);
      try {
        const payload = {
          pincode: Number(pincode),
          ...formData,
          codChargePercentage: Number(formData.codChargePercentage),
          codChargeTruckValue: Number(formData.codChargeTruckValue),
        };
  
        const baseUrl = process.env.REACT_APP_PATH_BULK || process.env.REACT_APP_PATH;
        await axios.post(`${baseUrl}/role4/addOrUpdatePincode`, payload);
        setSnackbar({ open: true, message: 'New Pincode added successfully.', severity: 'success' });
      } catch (error) {
        const msg = error.response?.data?.message || 'Something went wrong';
        setSnackbar({ open: true, message: msg, severity: 'error' });
      } finally {
        setSaving(false);
      }
    };

    const handleReset = () => {
        setPincode('');
        setFormData(initialFormState);
        setErrors({});
    };

    return (
      <SubCard title="Pincode Management">
              {/* Search Section */}
              <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', gap: 2, mb: 4, alignItems: 'flex-start' }}>
                  <TextField
                      label="Search Pincode"
                      value={pincode}
                      onChange={(e) => setPincode(e.target.value)}
                      required
                      fullWidth
                      error={!!errors.pincode}
                      helperText={errors.pincode}
                      inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
                  />
                  <Button 
                      variant="contained" 
                      size="large" 
                      type="submit" 
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <Search />}
                      sx={{ height: 56, minWidth: 120 }}
                  >
                      Search
                  </Button>
                  <Button 
                      variant="outlined" 
                      size="large" 
                      onClick={handleReset}
                      startIcon={<Replay />}
                      color="secondary"
                      sx={{ height: 56, minWidth: 120 }}
                  >
                      Reset
                  </Button>
              </Box>
  
              {/* Form Section */}
              <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                  Pincode Details
              </Typography>
              <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                      <TextField
                          fullWidth
                          label="Area Name"
                          name="areaName"
                          value={formData.areaName}
                          onChange={handleChange}
                          required
                          error={!!errors.areaName}
                          helperText={errors.areaName}
                      />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <TextField
                          fullWidth
                          label="Office Name"
                          name="officeName"
                          value={formData.officeName}
                          onChange={handleChange}
                          required
                          error={!!errors.officeName}
                          helperText={errors.officeName}
                      />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <TextField
                          fullWidth
                          label="Office Type"
                          name="officeType"
                          value={formData.officeType}
                          onChange={handleChange}
                          required
                          error={!!errors.officeType}
                          helperText={errors.officeType}
                      />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <TextField
                          fullWidth
                          label="Tehsil"
                          name="tehsil"
                          value={formData.tehsil}
                          onChange={handleChange}
                          required
                          error={!!errors.tehsil}
                          helperText={errors.tehsil}
                      />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <TextField
                          fullWidth
                          label="District"
                          name="district"
                          value={formData.district}
                          onChange={handleChange}
                          required
                          error={!!errors.district}
                          helperText={errors.district}
                      />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <TextField
                          fullWidth
                          label="Cluster"
                          name="cluster"
                          value={formData.cluster}
                          onChange={handleChange}
                          required
                          error={!!errors.cluster}
                          helperText={errors.cluster}
                      />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <TextField
                          fullWidth
                          label="State"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          required
                          error={!!errors.state}
                          helperText={errors.state}
                      />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <TextField
                          fullWidth
                          label="State ID (e.g., DL)"
                          name="stateId"
                          value={formData.stateId}
                          onChange={handleChange}
                          required
                          error={!!errors.stateId}
                          helperText={errors.stateId}
                          inputProps={{ maxLength: 2 }}
                      />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <TextField
                          fullWidth
                          label="COD Charge Percentage"
                          name="codChargePercentage"
                          type="number"
                          value={formData.codChargePercentage}
                          onChange={handleChange}
                          required
                          error={!!errors.codChargePercentage}
                          helperText={errors.codChargePercentage}
                      />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <TextField
                          fullWidth
                          label="COD Charge Truck Value"
                          name="codChargeTruckValue"
                          type="number"
                          value={formData.codChargeTruckValue}
                          onChange={handleChange}
                          required
                          error={!!errors.codChargeTruckValue}
                          helperText={errors.codChargeTruckValue}
                      />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <TextField
                          fullWidth
                          label="STD Code"
                          name="stdCode"
                          value={formData.stdCode}
                          onChange={handleChange}
                          required
                          error={!!errors.stdCode}
                          helperText={errors.stdCode}
                      />
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                          control={
                              <Switch
                                  checked={formData.pinCodeForPorterShipping}
                                  onChange={handleChange}
                                  name="pinCodeForPorterShipping"
                                  color="primary"
                              />
                          }
                          label="Porter Shipping"
                      />
                  </Grid>
              </Grid>
  
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                      onClick={handleSubmit}
                      disabled={saving}
                      sx={{ minWidth: 150 }}
                  >
                      Submit
                  </Button>
              </Box>
  
              <Snackbar
                  open={snackbar.open}
                  autoHideDuration={6000}
                  onClose={() => setSnackbar({ ...snackbar, open: false })}
                  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              >
                  <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                      {snackbar.message}
                  </Alert>
              </Snackbar>
      </SubCard>
    );
};

function MoveBulkStageOrder() {
  const [orderId, setOrderId] = useState("");
  const [revertOrderId,setRevertOrderId] =  useState('')
  const [loading, setLoading] = useState(false);
  const [revertLoading,setRevertLoading] = useState(false)
  const handelRevert = () => {
    let confirm = window.confirm('Do you want to revert this order? Please confirm.')
    if(!confirm) return
    setLoading(true);
    axios
      .post(process.env.REACT_APP_PATH_BULK + "/products/moveBulkOrderInStage1", {
        numericOrderId: revertOrderId,
      })
      .then((res) => {
        if (res.data.success) {
          alert(res.data?.message || "order revert successfully");
          window.location.reload();
          setLoading(false);
        } else {
          alert(res.data?.message || "Someting went wrong");
          setLoading(false);
        }
      })
      .catch((error) => {
        alert("Someting went wrong");
        setLoading(false);
      });
  };
  const handleMoveOrder = () => {
    let confirm = window.confirm('Are you sure , you want to Move this order?')
    if(!confirm) return
    setRevertLoading(true);
    axios
      .post(process.env.REACT_APP_PATH_BULK + "/products/moveOrderStageV2ToZero", {
        numericOrderId: orderId,
      })
      .then((res) => {
        if (res.data.success) {
          alert(res.data?.message || "Successfully Move Order in stage 1");
          window.location.reload();
          setLoading(false);
        } else {
          alert(res.data?.message || "Someting went wrong");
          setLoading(false);
        }
      })
      .catch((error) => {
        alert("Someting went wrong");
        setLoading(false);
      });
  };
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <SubCard title={"Revert Order"}>
          <Grid container>
            <TextField
              value={revertOrderId}
              onChange={(t) => setRevertOrderId(t.target.value)}
              title="Enter Order Id"
              sx={{ marginRight: 1 }}
              placeholder="Enter Order Id"
            />
            <LoadingButton
              loading={revertLoading}
              loadingIndicator="Loading..."
              variant="outlined"
              onClick={handelRevert}
            >
              Submit
            </LoadingButton>
          </Grid>
        </SubCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <SubCard title={"Move Bulk Order Stage in 0"}>
          <Grid container>
            <TextField
              value={orderId}
              onChange={(t) => setOrderId(t.target.value)}
              title="Enter Order Id"
              sx={{ marginRight: 1 }}
              placeholder="Enter Order Id"
              
            />
            <LoadingButton
              loading={loading}
              loadingIndicator="Loading..."
              variant="outlined"
              onClick={handleMoveOrder}
            >
              Submit
            </LoadingButton>
          </Grid>
        </SubCard>
      </Grid>
      
      {/* Pincode Management Section added below */}
      <Grid item xs={12}>
        <PincodeManagementSection />
      </Grid>
    </Grid>
  );
}

export default MoveBulkStageOrder;
